import { Fragment, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AssignedIndividualVolunteer,
  FunctionAssignmentPolicy,
} from '../GeneratedClient';
import { personNameString } from '../Families/PersonName';
import { useBackdrop } from '../Hooks/useBackdrop';
import { useAppNavigate } from '../Hooks/useAppNavigate';
import { useFunctionAssignmentCommands } from './useFunctionAssignmentCommands';
import { useFunctionAssignmentsEditorViewModel } from './useFunctionAssignmentsEditorViewModel';
import { useFunctionAssignmentsViewModel } from './useFunctionAssignmentsViewModel';

type FunctionAssignmentsSectionProps = {
  title?: string;
  assignments: AssignedIndividualVolunteer[];
  policies: FunctionAssignmentPolicy[];
  canEdit: boolean;
  onAssign: (personId: string, assignmentRole: string) => Promise<void>;
  onUnassign: (personId: string, assignmentRole: string) => Promise<void>;
};

type FunctionAssignmentsEditorDrawerProps = {
  title?: string;
  open: boolean;
  assignments: AssignedIndividualVolunteer[];
  policies: FunctionAssignmentPolicy[];
  onClose: () => void;
  onAssign: (personId: string, assignmentRole: string) => Promise<void>;
  onUnassign: (personId: string, assignmentRole: string) => Promise<void>;
};

export function FunctionAssignmentsEditorDrawer({
  title = 'Edit Function Assignments',
  open,
  assignments,
  policies,
  onClose,
  onAssign,
  onUnassign,
}: FunctionAssignmentsEditorDrawerProps) {
  const withBackdrop = useBackdrop();
  const [isSaving, setIsSaving] = useState(false);
  const { applyFunctionAssignmentChanges } = useFunctionAssignmentCommands({
    onAssign,
    onUnassign,
  });
  const { assignmentChanges, canSave, editorRows, updateAssignment } =
    useFunctionAssignmentsEditorViewModel({
      open,
      assignments,
      policies,
    });

  function closeDrawer() {
    if (isSaving) return;

    onClose();
  }

  async function saveAssignments() {
    setIsSaving(true);
    try {
      await withBackdrop(async () => {
        await applyFunctionAssignmentChanges(assignmentChanges);
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeDrawer}
      slotProps={{
        paper: {
          sx: {
            width: 500,
            p: 3,
            top: 45,
          },
        },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h6">{title}</Typography>

        {editorRows.map(({ assignmentRole, options, selectedCandidate }) => {
          return (
            <Box key={assignmentRole}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {assignmentRole}
              </Typography>

              <Autocomplete
                disablePortal
                value={selectedCandidate}
                options={options}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.personId === value.personId
                }
                groupBy={(option) => option.candidateType}
                disabled={isSaving}
                noOptionsText="No eligible people available"
                onChange={(_, candidate) => {
                  updateAssignment(assignmentRole, candidate);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={
                      options.length === 0
                        ? 'No eligible people available'
                        : 'Assigned person'
                    }
                  />
                )}
              />
            </Box>
          );
        })}

        <Stack
          direction="row"
          spacing={2}
          sx={{ justifyContent: 'flex-end' }}
        >
          <Button
            color="secondary"
            variant="contained"
            disabled={isSaving}
            onClick={closeDrawer}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isSaving || !canSave}
            onClick={() => {
              void saveAssignments();
            }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

export function FunctionAssignmentsSection({
  title = 'Function Assignments',
  assignments,
  policies,
  canEdit,
  onAssign,
  onUnassign,
}: FunctionAssignmentsSectionProps) {
  const appNavigate = useAppNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { assignedVolunteerRows, peopleById, roles } =
    useFunctionAssignmentsViewModel({
      assignments,
      policies,
    });

  function openDrawer() {
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', mb: 1 }}
      >
        <Typography variant="h3">{title}</Typography>
        {canEdit && (
          <Button variant="outlined" size="small" onClick={openDrawer}>
            Edit assignments
          </Button>
        )}
      </Stack>

      {roles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No function assignment roles configured.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {assignedVolunteerRows.map(({ assignedVolunteers, assignmentRole }) => {
            return (
              <Typography key={assignmentRole}>
                <strong>{assignmentRole}:</strong>{' '}
                {assignedVolunteers.length === 0
                  ? '—'
                  : assignedVolunteers.map((assignment, index) => {
                      const personEntry = peopleById.get(assignment.personId);
                      const name = personNameString(personEntry?.person);

                      return (
                        <Fragment
                          key={`${assignment.assignmentRole}:${assignment.personId}`}
                        >
                          {index > 0 && ', '}
                          {personEntry ? (
                            <Button
                              variant="text"
                              sx={{
                                p: 0,
                                minWidth: 'auto',
                                textTransform: 'none',
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: 'inherit',
                                verticalAlign: 'baseline',
                              }}
                              onClick={() =>
                                appNavigate.family(personEntry.familyId)
                              }
                            >
                              {name}
                            </Button>
                          ) : (
                            name
                          )}
                        </Fragment>
                      );
                    })}
              </Typography>
            );
          })}
        </Stack>
      )}

      <FunctionAssignmentsEditorDrawer
        open={drawerOpen}
        assignments={assignments}
        policies={policies}
        onClose={closeDrawer}
        onAssign={onAssign}
        onUnassign={onUnassign}
      />
    </Box>
  );
}
